import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Copy, CheckCircle, XCircle, Lock } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";

/* ────────────────────────────────────────────────────────────────────
   /cc/postnet — Postnet-branded operator issuance page.

   No-card variant: the Share A is sized for direct print on the
   certified document, alongside the existing "TRUE COPY · COMMISSIONER
   OF OATHS" stamp. Clerk fills the form, hits ISSUE, prints the
   resulting stamp card. The driver walks out with the certified
   document + the Curb Verify mark embedded in the corner.
   ──────────────────────────────────────────────────────────────────── */

const DOC_TYPES = [
  "ID Copy",
  "Driver's Licence",
  "Professional Driving Permit (PrDP)",
  "SAPS Police Clearance",
  "Proof of Residence",
  "Passport Copy",
  "Bank Letter",
  "Matric Certificate",
];

export default function PostnetIssue() {
  const [branch, setBranch] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverId, setDriverId] = useState("");
  const [docType, setDocType] = useState(DOC_TYPES[2]); // PrDP default
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  async function issue(e) {
    e.preventDefault();
    if (!branch || !driverName || !driverId || !docType) return;
    setBusy(true);
    setErr("");
    setIssued(null);
    try {
      // We pack the structured fields into the secret + label so the
      // verifier can echo them back at /cc/scan time. The doc_id is
      // server-generated (random 16-char) — Postnet prints it under
      // the Share A so the Uber agent has something to type.
      const secret = `PNET-${driverId.slice(-6)}-${docType.split(" ")[0].toUpperCase()}`.slice(0, 16);
      const label = `${branch} · ${driverName} · ${docType}`;
      const r = await api.post("/cc/partner/issue", { partner_id: "postnet", secret_text: secret, label });
      setIssued({ ...r.data, driverName, driverId, docType, branch });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyDocId() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.doc_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }

  function printStamp() {
    window.print();
  }

  function reset() {
    setIssued(null);
    setDriverName("");
    setDriverId("");
  }

  return (
    <div className="min-h-screen bg-white text-black">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white; }
          .print-stamp-card {
            page-break-inside: avoid;
            border: 2px solid black !important;
          }
        }
        .print-only { display: none; }
      `}</style>

      <header className="no-print bg-[#E30613] text-white border-b border-black">
        {/* Postnet red — matches their brand colour for the demo. */}
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2" data-testid="postnet-back">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2">
            POSTNET × CURB VERIFY
          </div>
          <Lock size={14} weight="bold" />
        </div>
      </header>

      <main className="no-print max-w-4xl mx-auto px-6 py-10">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-neutral-500">
          Operator dashboard
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">
          Issue a <span className="bg-[#E30613] text-white px-1">Curb Verified</span> stamp.
        </h1>
        <p className="mt-2 text-sm text-neutral-700 max-w-2xl">
          Fill in the driver details. Print the stamp onto the corner of the certified document, alongside the existing Commissioner of Oaths stamp.
        </p>

        {!issued && (
          <form onSubmit={issue} className="mt-8 border-2 border-black bg-neutral-50 p-6 space-y-4">
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Postnet branch</div>
              <input
                type="text"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="e.g. Boksburg North"
                className="mt-2 w-full bg-white border-2 border-black px-3 py-2 text-sm"
                required
                data-testid="postnet-branch"
              />
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Driver full name</div>
                <input
                  type="text"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  placeholder="e.g. Sipho Mthembu"
                  className="mt-2 w-full bg-white border-2 border-black px-3 py-2 text-sm"
                  required
                  data-testid="postnet-driver-name"
                />
              </label>
              <label className="block">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Driver ID number</div>
                <input
                  type="text"
                  value={driverId}
                  onChange={(e) => setDriverId(e.target.value.replace(/[^0-9]/g, "").slice(0, 13))}
                  placeholder="13 digit SA ID"
                  className="mt-2 w-full bg-white border-2 border-black px-3 py-2 text-sm font-mono"
                  required
                  data-testid="postnet-driver-id"
                />
              </label>
            </div>

            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Document type</div>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="mt-2 w-full bg-white border-2 border-black px-3 py-2 text-sm"
                data-testid="postnet-doc-type"
              >
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>

            <button
              type="submit"
              disabled={busy || !branch || !driverName || !driverId}
              className="inline-flex items-center gap-2 bg-[#E30613] text-white px-5 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-40"
              data-testid="postnet-issue-btn"
            >
              {busy ? "Generating stamp…" : "Generate Curb Verify stamp"}
            </button>

            {err && (
              <div className="border-2 border-red-600 bg-red-50 text-red-900 p-3 text-sm flex items-start gap-2">
                <XCircle size={16} weight="bold" className="shrink-0" /> {err}
              </div>
            )}
          </form>
        )}

        {issued && (
          <div className="mt-8" data-testid="postnet-issued">
            <div className="flex items-center gap-2 text-[#E30613]">
              <CheckCircle size={20} weight="bold" />
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase">Stamp generated · ready to print</div>
            </div>

            {/* This is the artifact that will physically print on the document */}
            <div className="mt-6 print-stamp-card bg-white border-2 border-black p-6 mx-auto" style={{ maxWidth: 420 }}>
              <div className="flex items-start gap-4">
                <img
                  src={`data:image/png;base64,${issued.share_a_png_b64}`}
                  alt="Curb Verify Share A"
                  style={{ width: 120, height: 120 }}
                  className="block border border-black"
                  data-testid="postnet-share-a"
                />
                <div className="flex-1 text-xs leading-tight">
                  <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-neutral-500">Curb Verify</div>
                  <div className="font-display text-base mt-1">CERTIFIED TRUE COPY</div>
                  <div className="mt-2 space-y-0.5 font-mono">
                    <div><span className="text-neutral-500">DOC:</span> {issued.docType}</div>
                    <div><span className="text-neutral-500">SUBJ:</span> {issued.driverName}</div>
                    <div><span className="text-neutral-500">ID:</span> {issued.driverId}</div>
                    <div><span className="text-neutral-500">BR:</span> {issued.branch}</div>
                    <div><span className="text-neutral-500">REF:</span> <strong>{issued.doc_id}</strong></div>
                    <div><span className="text-neutral-500">DATE:</span> {new Date().toLocaleDateString()}</div>
                  </div>
                  <div className="mt-2 text-[9px] text-neutral-600 leading-snug">
                    Verify at <strong>curbcrew.co.za/cc/scan</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 no-print flex flex-wrap gap-3">
              <button
                onClick={printStamp}
                className="inline-flex items-center gap-2 bg-black text-white px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
                data-testid="postnet-print-btn"
              >
                <Printer size={14} weight="bold" /> Print stamp
              </button>
              <button
                onClick={copyDocId}
                className="inline-flex items-center gap-2 border border-black px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
                data-testid="postnet-copy-docid"
              >
                <Copy size={12} weight="bold" /> Copy doc ID {copied && <span>· COPIED</span>}
              </button>
              <Link
                to="/cc/scan"
                className="inline-flex items-center gap-2 border border-black px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
              >
                Open verifier
              </Link>
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 border border-black px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
                data-testid="postnet-reset"
              >
                Issue next driver
              </button>
            </div>

            <div className="mt-6 bg-neutral-100 border border-neutral-300 p-4 text-xs text-neutral-700 leading-relaxed max-w-2xl">
              <strong>Workflow:</strong> Print this stamp. Glue or staple to the bottom-right corner of the certified document, next to the existing TRUE COPY stamp. Driver pays the Curb Verify line item (R10-20). When Uber / Bolt receives the document they verify in 15 seconds at <code className="font-mono">curbcrew.co.za/cc/scan</code> using the REF code above.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
